/** The encounter library endpoint and the grid-mode vocabulary its records carry. */

/**
 * An encounter's **grid mode** — the dimensionality × cell shape of its map. The matrix is
 * `{2d, 3d} × {square, hex}`; we list the meaningful combinations. Stored on the encounter at
 * creation and immutable for v1.
 *
 * **v1 supports `2d-square` only.** The other modes are listed (so the create picker can show
 * the roadmap and reserve the values) but are **not yet creatable** — `isSupportedGridMode`
 * gates them, and the create endpoint rejects an unsupported mode.
 */
export type GridMode = "2d-square" | "2d-hex" | "3d-hex";

/** Every grid mode, with a display label and whether v1 can actually create it. */
export const GRID_MODES: readonly { value: GridMode; label: string; supported: boolean }[] = [
  { value: "2d-square", label: "2D · Square", supported: true },
  { value: "2d-hex", label: "2D · Hex", supported: false },
  { value: "3d-hex", label: "3D · Hex", supported: false },
];

/** The default grid mode for a fresh encounter (the one mode v1 supports). */
export const DEFAULT_GRID_MODE: GridMode = "2d-square";

/** Whether a string is a known {@link GridMode} (any mode, supported or not). */
export function isGridMode(m: string): m is GridMode {
  return GRID_MODES.some((g) => g.value === m);
}

/** Whether a string is a grid mode v1 can **create** (only `2d-square` today). */
export function isSupportedGridMode(m: string): m is GridMode {
  return GRID_MODES.some((g) => g.value === m && g.supported);
}

/**
 * The **encounter library** REST endpoint (encounter CRUD). World-folder-backed (one
 * `<world>/encounters/<id>.{json,log}` per encounter); list is public lobby metadata, while
 * create (POST) and delete (DELETE) are **GM-only** (the host key in {@link HOST_KEY_HEADER},
 * the same operator credential the scenes endpoints use). An encounter is created as an empty
 * snapshot (name + grid mode); combatants/terrain are filled later over the encounter ws.
 *
 *   GET    /api/world/encounters        → {@link EncounterListResponse} (summaries; public)
 *   POST   /api/world/encounters        → create (GM host key); body {@link CreateEncounterRequest}
 *   DELETE /api/world/encounters/<id>   → delete (GM host key)
 */
export const WORLD_ENCOUNTERS_PATH = "/api/world/encounters" as const;

/** A lightweight encounter descriptor for the library list (no combatants/terrain). */
export type EncounterSummary = {
  id: string;
  name: string;
  gridMode: GridMode;
  /** When the GM ended the fight (`end_combat`), absent while it is live. */
  combatEndedAt?: string;
}

/** The {@link WORLD_ENCOUNTERS_PATH} list response: every encounter on the world server (possibly none). */
export type EncounterListResponse = {
  encounters: EncounterSummary[];
}

/** The POST {@link WORLD_ENCOUNTERS_PATH} body — the basic config a new encounter is created with. */
export type CreateEncounterRequest = {
  name: string;
  gridMode: GridMode;
}

/** The POST {@link WORLD_ENCOUNTERS_PATH} response — the freshly created encounter's summary (server-minted id). */
export type CreateEncounterResponse = {
  encounter: EncounterSummary;
}